import React, { forwardRef, memo, useCallback, useEffect, useId, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { IconMaximize as Maximize, IconMinus as Minus, IconPlus as Plus, IconScanEye as ScanSearch } from '@tabler/icons-react';
import {
    add, boundsOf, dist, pointInRect, rectFromPoints, rectsIntersect, rotatePoint, rowLabelPositions, SEAT_RADIUS,
    seatAbs, seatKey, shapeBounds, shapeCenter, shapeKey, snapAngle, snapPoint, snapValue, sub,
    type Rect,
} from './geometry';
import { linkTables, minTableSize, tableLayout, tableShapeOf } from './planOps';
import { fullZoneIndexes, sectionLabelAnchor, zoneSeatKeys } from './sections';
import { gaCapacityText, ShapeContent, shapeIconId } from './objectIcons';
import type { EditorSettings, Plan, PlanIndex, Point, Row, Selection, Shape, ShapeKind, Stage, TableShape, Tool, View, Zone } from './types';

export type CanvasHandle = {
    fit: () => void;
    viewCenter: () => Point;
    revealSelection: () => void;
    zoomToSelection: () => void;
    zoomBy: (factor: number) => void;
    resetZoom: () => void;
};

type Props = {
    plan: Plan;
    index: PlanIndex;
    selection: Selection;
    tool: Tool;
    settings: EditorSettings;
    spaceDown: boolean;
    focusSignal: number;
    onSelect: (sel: Selection) => void;
    onMove: (d: Point) => void;
    onRotate: (center: Point, deg: number) => void;
    onResizeShape: (key: string, rect: Rect) => void;
    onResizeTable: (key: string, rect: Rect) => void;
    onDrawRows: (rows: Point[][]) => void;
    onDrawShape: (kind: ShapeKind, rect: Rect) => void;
    onDrawPolygon: (points: Point[]) => void;
    onPlace: (tool: 'table' | 'text', p: Point) => void;
};

type Interaction =
    | { type: 'pan'; startScreen: Point; startView: View }
    | { type: 'pinch'; dist: number; mid: Point; view: View }
    | { type: 'move'; start: Point; delta: Point; moved: boolean }
    | { type: 'marquee'; a: Point; b: Point; additive: boolean }
    | { type: 'rotate'; center: Point; startAngle: number; angle: number }
    | { type: 'resize'; key: string; anchor: Point; rect: Rect; min: { width: number; height: number }; ratio: number; table: { shape: TableShape; count: number; ends: number } | null }
    | { type: 'row'; a: Point; b: Point }
    | { type: 'block'; a: Point; b: Point }
    | { type: 'shape'; kind: ShapeKind; a: Point; b: Point };

const MIN_K = 0.05;
const MAX_K = 8;
const clampK = (k: number) => Math.min(MAX_K, Math.max(MIN_K, k));

const TOOL_HINTS: Record<Tool, string> = {
    select: 'Click to select a section or row, double-click to drill into rows and seats, Alt+click for a single seat, drag empty space to box-select',
    pan: 'Drag to pan. Hold Space with any tool to pan temporarily',
    row: 'Drag to draw a row of seats. Hold Shift to lock the angle to 15 degree steps',
    block: 'Drag a rectangle to fill it with rows of seats',
    seat: 'Click to place a single seat',
    table: 'Click to place a table. Set seats per table and shape in the right panel',
    text: 'Click to place a text label',
    stage: 'Drag to draw a stage, or click for a default size',
    polygon: 'Click to add points. Click the first point, double-click or press Enter to finish. Esc cancels',
    entrance: 'Drag to draw an entrance, or click for a default size',
};

type RowViewProps = {
    row: Row;
    zone: Zone;
    colors: Map<string, string>;
    selected?: Set<string>;
    preview?: (p: Point) => Point;
    showNumbers: boolean;
    showLabels: boolean;
};

const RowView = memo(({ row, zone, colors, selected, preview, showNumbers, showLabels }: RowViewProps) => {
    const points = row.seats.map(s => {
        const p = seatAbs(zone, row, s);
        return preview && selected?.has(seatKey(s)) ? preview(p) : p;
    });
    const labels = showLabels && !row.tableId ? rowLabelPositions(points) : null;
    return (
        <g>
            {labels && row.row_number && (
                <>
                    <text data-row={row.uuid} x={labels.left.x} y={labels.left.y} dy=".35em" textAnchor="middle" className="fill-gray-500 cursor-pointer select-none" fontSize={11} fontWeight={600}>
                        {row.row_number}
                    </text>
                    {row.seats.length > 1 && (
                        <text data-row={row.uuid} x={labels.right.x} y={labels.right.y} dy=".35em" textAnchor="middle" className="fill-gray-500 cursor-pointer select-none" fontSize={11} fontWeight={600}>
                            {row.row_number}
                        </text>
                    )}
                </>
            )}
            {row.seats.map((s, i) => {
                const key = seatKey(s);
                const p = points[i];
                const isSel = selected?.has(key);
                const color = s.category ? colors.get(s.category) : undefined;
                return (
                    <g key={key || i}>
                        <circle
                            data-seat={key}
                            cx={p.x}
                            cy={p.y}
                            r={SEAT_RADIUS}
                            fill={color ?? '#ffffff'}
                            stroke={isSel ? '#0EA5E9' : color ? 'rgba(0,0,0,0.35)' : '#9CA3AF'}
                            strokeWidth={isSel ? 3 : 1}
                            className="cursor-pointer"
                        />
                        {s.accessible && (
                            <g pointerEvents="none">
                                <circle cx={p.x + 7.5} cy={p.y - 7.5} r={4.5} fill="#2563EB" stroke="#ffffff" strokeWidth={1.2} />
                                <circle cx={p.x + 7.5} cy={p.y - 9} r={0.9} fill="#ffffff" />
                                <path d={`M ${p.x + 7.5} ${p.y - 7.8} v 2.2 h 1.6`} stroke="#ffffff" strokeWidth={0.9} fill="none" />
                            </g>
                        )}
                        {showNumbers && (
                            <text x={p.x} y={p.y} dy=".35em" textAnchor="middle" fontSize={9} fontWeight={600} fill={color ? '#ffffff' : '#4B5563'} pointerEvents="none" className="select-none">
                                {s.seat_number}
                            </text>
                        )}
                    </g>
                );
            })}
        </g>
    );
});

const ShapeView: React.FC<{ shape: Shape; kind: ShapeKind; selected: boolean }> = ({ shape, kind, selected }) => {
    const { width: w, height: h } = shape.size;
    const fill = shape.color || (kind === 'stage' ? '#FDE68A' : '#BBF7D0');
    const stroke = shape.strokeColor || (kind === 'stage' ? '#D97706' : '#16A34A');
    const sw = shape.strokeWidth ?? 2;
    const key = shapeKey(kind, shape.id);
    const stage = shape as Stage;
    const transform = `translate(${shape.position.x} ${shape.position.y}) rotate(${shape.rotation || 0} ${w / 2} ${h / 2})`;
    if (stage.variant === 'text') {
        return (
            <g data-shape={key} className="cursor-move" transform={transform}>
                <rect width={w} height={h} fill="transparent" stroke={selected ? '#0EA5E9' : '#9CA3AF'} strokeOpacity={selected ? 1 : 0.5} strokeWidth={1} strokeDasharray="3,3" />
                <text x={w / 2} y={h / 2} dy=".35em" textAnchor="middle" fontSize={stage.fontSize ?? 14} fontWeight={700} className="fill-gray-800 select-none" pointerEvents="none">
                    {shape.name}
                </text>
            </g>
        );
    }
    if (stage.variant === 'line' && shape.points) {
        const pts = shape.points.map(p => `${p.x},${p.y}`).join(' ');
        return (
            <g data-shape={key} className="cursor-move" transform={transform}>
                <polyline points={pts} fill="none" stroke="transparent" strokeWidth={Math.max(14, sw + 10)} strokeLinecap="round" />
                <polyline points={pts} fill="none" stroke={selected ? '#0EA5E9' : stroke} strokeWidth={Math.max(1, sw)} strokeLinecap="round" pointerEvents="none" />
            </g>
        );
    }
    return (
        <g data-shape={key} className="cursor-move" transform={transform}>
            {shape.type === 'circle' && <ellipse cx={w / 2} cy={h / 2} rx={w / 2} ry={h / 2} fill={fill} stroke={stroke} strokeWidth={sw} />}
            {shape.type === 'polygon' && shape.points && (
                <polygon points={shape.points.map(p => `${p.x},${p.y}`).join(' ')} fill={fill} stroke={stroke} strokeWidth={sw} />
            )}
            {shape.type === 'square' && (
                <rect
                    width={w}
                    height={h}
                    rx={'curveRadius' in shape ? shape.curveRadius || 0 : 0}
                    fill={fill}
                    stroke={stroke}
                    strokeWidth={sw}
                    strokeDasharray={kind === 'entrance' ? '5,5' : undefined}
                />
            )}
            <ShapeContent width={w} height={h} name={shape.name} iconId={shapeIconId(stage)} sub={gaCapacityText(stage)} fontSize={kind === 'stage' ? 14 : 12} />
            {selected && <rect x={-3} y={-3} width={w + 6} height={h + 6} fill="none" stroke="#0EA5E9" strokeWidth={1.5} strokeDasharray="4,3" pointerEvents="none" />}
        </g>
    );
};

const previewShape = (s: Shape, ix: Interaction | null): Shape => {
    if (!ix) return s;
    if (ix.type === 'move') return { ...s, position: add(s.position, ix.delta) };
    if (ix.type === 'rotate') {
        if (s.type === 'polygon' && s.points) {
            const c = shapeCenter(s);
            const pc = rotatePoint(c, ix.center, ix.angle);
            return { ...s, position: add(s.position, sub(pc, c)), rotation: (s.rotation || 0) + ix.angle };
        }
        const c = rotatePoint(shapeCenter(s), ix.center, ix.angle);
        return { ...s, rotation: (s.rotation || 0) + ix.angle, position: { x: c.x - s.size.width / 2, y: c.y - s.size.height / 2 } };
    }
    if (ix.type === 'resize') {
        const sx = s.size.width ? ix.rect.width / s.size.width : 1;
        const sy = s.size.height ? ix.rect.height / s.size.height : 1;
        return {
            ...s,
            position: { x: ix.rect.x, y: ix.rect.y },
            size: { width: ix.rect.width, height: ix.rect.height },
            points: s.points?.map(p => ({ x: p.x * sx, y: p.y * sy })),
        };
    }
    return s;
};

const linePoints = (a: Point, b: Point, spacing: number): Point[] => {
    const len = dist(a, b);
    const n = Math.floor(len / spacing + 0.0001) + 1;
    const u = len ? { x: (b.x - a.x) / len, y: (b.y - a.y) / len } : { x: 1, y: 0 };
    return Array.from({ length: n }, (_, i) => ({ x: a.x + u.x * spacing * i, y: a.y + u.y * spacing * i }));
};

const blockRows = (a: Point, b: Point, seatSpacing: number, rowSpacing: number): Point[][] => {
    const r = rectFromPoints(a, b);
    const cols = Math.floor(r.width / seatSpacing + 0.0001) + 1;
    const rows = Math.floor(r.height / rowSpacing + 0.0001) + 1;
    return Array.from({ length: rows }, (_, ri) =>
        Array.from({ length: cols }, (_, ci) => ({ x: r.x + ci * seatSpacing, y: r.y + ri * rowSpacing })),
    );
};

const Canvas = forwardRef<CanvasHandle, Props>((props, ref) => {
    const {
        plan, index, selection, tool, settings, spaceDown, focusSignal,
        onSelect, onMove, onRotate, onResizeShape, onResizeTable, onDrawRows, onDrawShape, onDrawPolygon, onPlace,
    } = props;
    const gridId = useId().replace(/:/g, '');
    const wrapRef = useRef<HTMLDivElement>(null);
    const svgRef = useRef<SVGSVGElement>(null);
    const [size, setSize] = useState({ width: 800, height: 600 });
    const [view, setView] = useState<View>({ x: 40, y: 40, k: 1 });
    const viewRef = useRef(view);
    viewRef.current = view;
    const [ix, setIxState] = useState<Interaction | null>(null);
    const ixRef = useRef<Interaction | null>(null);
    const setIx = (next: Interaction | null) => {
        ixRef.current = next;
        setIxState(next);
    };
    const [cursor, setCursor] = useState<Point | null>(null);
    const [polygon, setPolygon] = useState<Point[]>([]);
    const coarse = useMemo(() => typeof window !== 'undefined' && !!window.matchMedia?.('(pointer: coarse)').matches, []);
    const didFit = useRef(false);

    const colors = useMemo(() => new Map(plan.categories.map(c => [c.name, c.color])), [plan.categories]);
    const sectionAnchors = useMemo(() => plan.zones.map(z => (z.section ? sectionLabelAnchor(z) : null)), [plan.zones]);
    const selectedZones = useMemo(() => fullZoneIndexes(plan, selection.seats), [plan, selection.seats]);

    const selectionByRow = useMemo(() => {
        const map = new Map<string, Set<string>>();
        selection.seats.forEach(k => {
            const r = index.seatRow.get(k);
            if (!r) return;
            if (!map.has(r)) map.set(r, new Set());
            map.get(r)!.add(k);
        });
        return map;
    }, [selection.seats, index]);

    const selectionBounds = useMemo(() => {
        const pts: Point[] = [];
        selection.seats.forEach(k => {
            const ref = index.seats.get(k);
            if (!ref) return;
            const zone = plan.zones[ref.zi];
            const row = zone.rows[ref.ri];
            pts.push(seatAbs(zone, row, row.seats[ref.si]));
        });
        selectedZones.forEach(zi => {
            const a = sectionAnchors[zi];
            if (a) pts.push({ x: a.point.x, y: a.point.y - 6 });
        });
        const seatBox = boundsOf(pts, SEAT_RADIUS + 4);
        const boxes: Rect[] = seatBox ? [seatBox] : [];
        selection.shapes.forEach(k => {
            const ref = index.shapes.get(k);
            if (!ref) return;
            const s = ref.kind === 'stage' ? plan.stages![ref.index] : plan.entrances![ref.index];
            boxes.push(shapeBounds(s));
        });
        if (!boxes.length) return null;
        return boundsOf(boxes.flatMap(b => [{ x: b.x, y: b.y }, { x: b.x + b.width, y: b.y + b.height }]));
    }, [selection, index, plan, selectedZones, sectionAnchors]);

    const singleShape = useMemo(() => {
        if (selection.shapes.size !== 1) return null;
        const key = [...selection.shapes][0];
        const ref = index.shapes.get(key);
        if (!ref) return null;
        const s = ref.kind === 'stage' ? plan.stages![ref.index] : plan.entrances![ref.index];
        if ((s.rotation || 0) % 360 !== 0) return null;
        const tableRow = ref.kind === 'stage' && (s as Stage).variant === 'table' ? (s as Stage).tableRow : undefined;
        if (!tableRow) return selection.seats.size ? null : { key, shape: s, table: null };
        const rowRef = index.rows.get(tableRow);
        const row = rowRef ? plan.zones[rowRef.zi].rows[rowRef.ri] : undefined;
        if (!row || row.seats.length !== selection.seats.size || !row.seats.every(st => selection.seats.has(seatKey(st)))) return null;
        const shape = tableShapeOf(s as Stage);
        return { key, shape: s, table: { shape, count: row.seats.length, ends: shape === 'rect' ? (s as Stage).endSeats ?? 0 : 0 } };
    }, [selection, index, plan]);

    const contentBounds = useCallback(() => {
        const pts: Point[] = [];
        plan.zones.forEach(z => z.rows.forEach(r => r.seats.forEach(s => pts.push(seatAbs(z, r, s)))));
        [...(plan.stages ?? []), ...(plan.entrances ?? [])].forEach(s => {
            const b = shapeBounds(s);
            pts.push({ x: b.x, y: b.y }, { x: b.x + b.width, y: b.y + b.height });
        });
        return boundsOf(pts, SEAT_RADIUS + 30);
    }, [plan]);

    const fit = useCallback(() => {
        const b = contentBounds();
        const { width, height } = wrapRef.current?.getBoundingClientRect() ?? size;
        if (!b || !width || !height) {
            setView({ x: width / 2, y: height / 2, k: 1 });
            return;
        }
        const k = clampK(Math.min(width / b.width, height / b.height) * 0.92);
        setView({ x: width / 2 - (b.x + b.width / 2) * k, y: height / 2 - (b.y + b.height / 2) * k, k });
    }, [contentBounds, size]);

    const zoomAt = useCallback((factor: number, screen?: Point) => {
        setView(v => {
            const k = clampK(v.k * factor);
            const c = screen ?? { x: size.width / 2, y: size.height / 2 };
            const wx = (c.x - v.x) / v.k;
            const wy = (c.y - v.y) / v.k;
            return { k, x: c.x - wx * k, y: c.y - wy * k };
        });
    }, [size]);

    const animRef = useRef<number | null>(null);
    const stopAnimation = () => {
        if (animRef.current !== null) cancelAnimationFrame(animRef.current);
        animRef.current = null;
    };

    const animateTo = useCallback((target: View) => {
        stopAnimation();
        const { width: W, height: H } = size;
        const from = viewRef.current;
        const c0 = { x: (W / 2 - from.x) / from.k, y: (H / 2 - from.y) / from.k };
        const c1 = { x: (W / 2 - target.x) / target.k, y: (H / 2 - target.y) / target.k };
        const start = performance.now();
        const step = (now: number) => {
            const t = Math.min(1, (now - start) / 280);
            const e = 1 - Math.pow(1 - t, 3);
            const kk = from.k * Math.pow(target.k / from.k, e);
            const cx = c0.x + (c1.x - c0.x) * e;
            const cy = c0.y + (c1.y - c0.y) * e;
            setView({ k: kk, x: W / 2 - cx * kk, y: H / 2 - cy * kk });
            animRef.current = t < 1 ? requestAnimationFrame(step) : null;
        };
        animRef.current = requestAnimationFrame(step);
    }, [size]);

    const focusRect = useCallback((rect: Rect | null, mode: 'always' | 'ifNeeded' | 'visible') => {
        const { width: W, height: H } = size;
        if (!rect || !W || !H) return;
        const v = viewRef.current;
        const sx = rect.x * v.k + v.x;
        const sy = rect.y * v.k + v.y;
        const sw = rect.width * v.k;
        const sh = rect.height * v.k;
        const visible = sx >= 0 && sy >= 0 && sx + sw <= W && sy + sh <= H;
        const small = Math.max(sw / W, sh / H) < 0.3;
        if (mode === 'ifNeeded' && visible && !small) return;
        if (mode === 'visible' && visible) return;
        const pad = 90;
        const fitK = Math.min((W - pad * 2) / Math.max(rect.width, 40), (H - pad * 2) / Math.max(rect.height, 40));
        const k = clampK(mode === 'visible' ? Math.min(fitK, v.k) : Math.min(fitK, 2.5));
        const target = { k, x: W / 2 - (rect.x + rect.width / 2) * k, y: H / 2 - (rect.y + rect.height / 2) * k };
        if (Math.abs(target.k - v.k) / v.k < 0.02 && Math.hypot(target.x - v.x, target.y - v.y) < 4) return;
        animateTo(target);
    }, [size, animateTo]);

    const pendingFocus = useRef<'always' | 'ifNeeded' | 'visible' | null>(null);
    const selectionBoundsRef = useRef(selectionBounds);
    selectionBoundsRef.current = selectionBounds;
    const clickFocusTimer = useRef<number | null>(null);
    const cancelClickFocus = () => {
        if (clickFocusTimer.current !== null) window.clearTimeout(clickFocusTimer.current);
        clickFocusTimer.current = null;
    };
    useEffect(() => {
        if (focusSignal) pendingFocus.current = 'always';
    }, [focusSignal]);
    useEffect(() => {
        const mode = pendingFocus.current;
        if (!mode) return;
        pendingFocus.current = null;
        focusRect(selectionBounds, mode);
    }, [selectionBounds, focusSignal, focusRect]);

    useEffect(() => () => {
        stopAnimation();
        cancelClickFocus();
    }, []);

    useImperativeHandle(ref, () => ({
        fit,
        viewCenter: () => {
            const v = viewRef.current;
            return { x: (size.width / 2 - v.x) / v.k, y: (size.height / 2 - v.y) / v.k };
        },
        revealSelection: () => {
            pendingFocus.current = 'visible';
        },
        zoomToSelection: () => focusRect(selectionBounds, 'always'),
        zoomBy: f => zoomAt(f),
        resetZoom: () => zoomAt(1 / viewRef.current.k),
    }), [fit, zoomAt, focusRect, selectionBounds, size]);

    useEffect(() => {
        const el = wrapRef.current;
        if (!el) return;
        const ro = new ResizeObserver(([entry]) => {
            const { width, height } = entry.contentRect;
            setSize({ width, height });
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    useEffect(() => {
        if (didFit.current || size.width < 10) return;
        didFit.current = true;
        fit();
    }, [fit, size.width]);

    useEffect(() => {
        const el = svgRef.current;
        if (!el) return;
        const onWheel = (e: WheelEvent) => {
            e.preventDefault();
            stopAnimation();
            const rect = el.getBoundingClientRect();
            if (e.ctrlKey || e.metaKey) {
                zoomAt(Math.exp(-e.deltaY * 0.01), { x: e.clientX - rect.left, y: e.clientY - rect.top });
            } else {
                const dx = e.shiftKey && !e.deltaX ? e.deltaY : e.deltaX;
                const dy = e.shiftKey && !e.deltaX ? 0 : e.deltaY;
                setView(v => ({ ...v, x: v.x - dx, y: v.y - dy }));
            }
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, [zoomAt]);

    const finishPolygon = useCallback((pts: Point[]) => {
        if (pts.length >= 3) onDrawPolygon(pts);
        setPolygon([]);
    }, [onDrawPolygon]);

    useEffect(() => {
        if (tool !== 'polygon') setPolygon([]);
    }, [tool]);

    useEffect(() => {
        if (tool !== 'polygon' || !polygon.length) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Enter') finishPolygon(polygon);
            else if (e.key === 'Escape') setPolygon([]);
            else if (e.key === 'Backspace') {
                e.preventDefault();
                e.stopPropagation();
                setPolygon(p => p.slice(0, -1));
            }
        };
        window.addEventListener('keydown', onKey, true);
        return () => window.removeEventListener('keydown', onKey, true);
    }, [tool, polygon, finishPolygon]);

    const toWorld = (e: { clientX: number; clientY: number }): Point => {
        const rect = svgRef.current!.getBoundingClientRect();
        const v = viewRef.current;
        return { x: (e.clientX - rect.left - v.x) / v.k, y: (e.clientY - rect.top - v.y) / v.k };
    };

    const snapIf = (p: Point) => (settings.snap ? snapPoint(p, settings.gridSize) : p);

    const rowSeatsOf = (rowId: string): string[] => {
        const ref = index.rows.get(rowId);
        if (!ref) return [];
        return plan.zones[ref.zi].rows[ref.ri].seats.map(seatKey);
    };

    const pickTarget = (target: Element, alt: boolean, dbl: boolean): Selection | null => {
        const picked = pickRaw(target, alt, dbl);
        return picked && !(alt || dbl) ? linkTables(plan, index, picked) : picked;
    };

    const pickRaw = (target: Element, alt: boolean, dbl: boolean): Selection | null => {
        const seatEl = target.closest('[data-seat]');
        if (seatEl) {
            const key = seatEl.getAttribute('data-seat')!;
            const seatSel: Selection = { seats: new Set([key]), shapes: new Set() };
            const rowId = index.seatRow.get(key);
            const rowSel: Selection = { seats: new Set(rowId ? rowSeatsOf(rowId) : [key]), shapes: new Set() };
            const ref = index.seats.get(key);
            const zone = ref ? plan.zones[ref.zi] : undefined;
            if (zone?.section) {
                if (alt) return seatSel;
                if (dbl) {
                    const rowSelected = rowSel.seats.size === selection.seats.size && [...rowSel.seats].every(k => selection.seats.has(k));
                    return rowSelected ? seatSel : rowSel;
                }
                return { seats: new Set(zoneSeatKeys(zone)), shapes: new Set() };
            }
            if (alt || dbl) return seatSel;
            return rowSel;
        }
        const zoneEl = target.closest('[data-zone]');
        if (zoneEl) {
            const zone = plan.zones[Number(zoneEl.getAttribute('data-zone'))];
            if (zone) return { seats: new Set(zoneSeatKeys(zone)), shapes: new Set() };
        }
        const rowEl = target.closest('[data-row]');
        if (rowEl) return { seats: new Set(rowSeatsOf(rowEl.getAttribute('data-row')!)), shapes: new Set() };
        const shapeEl = target.closest('[data-shape]');
        if (shapeEl) return { seats: new Set(), shapes: new Set([shapeEl.getAttribute('data-shape')!]) };
        return null;
    };

    const contains = (sel: Selection, t: Selection) =>
        [...t.seats].every(k => sel.seats.has(k)) && [...t.shapes].every(k => sel.shapes.has(k));

    const lastPress = useRef<{ x: number; y: number; t: number } | null>(null);
    const isDoublePress = (e: React.PointerEvent) => {
        const now = performance.now();
        const prev = lastPress.current;
        const dbl = e.detail >= 2 || (!!prev && now - prev.t < 350 && Math.hypot(e.clientX - prev.x, e.clientY - prev.y) < 6);
        lastPress.current = dbl ? null : { x: e.clientX, y: e.clientY, t: now };
        return dbl;
    };

    const pointers = useRef(new Map<number, Point>());
    const touchTap = useRef<{ sp: Point; dbl: boolean; x: number; y: number } | null>(null);

    const localPoint = (e: { clientX: number; clientY: number }): Point => {
        const r = svgRef.current!.getBoundingClientRect();
        return { x: e.clientX - r.left, y: e.clientY - r.top };
    };

    const placeAt = (sp: Point, dbl: boolean) => {
        if (tool === 'seat') onDrawRows([[sp]]);
        else if (tool === 'table' || tool === 'text') onPlace(tool, sp);
        else if (tool === 'polygon') {
            if (dbl || (polygon.length >= 3 && dist(sp, polygon[0]) * viewRef.current.k < (coarse ? 22 : 10))) {
                finishPolygon(polygon);
                return;
            }
            setPolygon(pts => [...pts, sp]);
        }
    };

    const onPointerDown = (e: React.PointerEvent<SVGSVGElement>) => {
        stopAnimation();
        cancelClickFocus();
        pendingFocus.current = null;
        pointers.current.set(e.pointerId, localPoint(e));
        if (pointers.current.size >= 2) {
            svgRef.current!.setPointerCapture(e.pointerId);
            const [a, b] = [...pointers.current.values()];
            touchTap.current = null;
            setIx({ type: 'pinch', dist: Math.max(1, Math.hypot(a.x - b.x, a.y - b.y)), mid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }, view: viewRef.current });
            return;
        }
        const dbl = e.button === 0 && isDoublePress(e);
        const p = toWorld(e);
        const panning = e.button === 1 || tool === 'pan' || spaceDown;
        if (panning) {
            e.preventDefault();
            svgRef.current!.setPointerCapture(e.pointerId);
            setIx({ type: 'pan', startScreen: { x: e.clientX, y: e.clientY }, startView: viewRef.current });
            return;
        }
        if (e.button !== 0) return;
        svgRef.current!.setPointerCapture(e.pointerId);
        const target = e.target as Element;

        if (tool === 'select') {
            const handle = target.closest('[data-handle]')?.getAttribute('data-handle');
            if (handle === 'rotate' && selectionBounds) {
                const center = { x: selectionBounds.x + selectionBounds.width / 2, y: selectionBounds.y + selectionBounds.height / 2 };
                setIx({ type: 'rotate', center, startAngle: Math.atan2(p.y - center.y, p.x - center.x), angle: 0 });
                return;
            }
            if (handle && singleShape) {
                const { position: pos, size: sz } = singleShape.shape;
                const anchor = {
                    x: handle.includes('w') ? pos.x + sz.width : pos.x,
                    y: handle.includes('n') ? pos.y + sz.height : pos.y,
                };
                const { table } = singleShape;
                const isLine = (singleShape.shape as Stage).variant === 'line';
                const min = table ? minTableSize(table.shape, table.count, settings.seatSpacing, table.ends) : { width: 10, height: isLine ? 0 : 10 };
                setIx({ type: 'resize', key: singleShape.key, anchor, rect: { x: pos.x, y: pos.y, ...sz }, min, ratio: sz.width / (sz.height || 1), table });
                return;
            }
            const picked = pickTarget(target, e.altKey, dbl);
            const toggle = e.shiftKey || e.metaKey || e.ctrlKey;
            if (picked) {
                if (toggle) {
                    const has = contains(selection, picked);
                    const seats = new Set(selection.seats);
                    const shapes = new Set(selection.shapes);
                    picked.seats.forEach(k => (has ? seats.delete(k) : seats.add(k)));
                    picked.shapes.forEach(k => (has ? shapes.delete(k) : shapes.add(k)));
                    onSelect({ seats, shapes });
                    return;
                }
                if (dbl || !contains(selection, picked)) onSelect(picked);
                setIx({ type: 'move', start: p, delta: { x: 0, y: 0 }, moved: false });
                return;
            }
            setIx({ type: 'marquee', a: p, b: p, additive: toggle });
            return;
        }

        const sp = snapIf(p);
        if (tool === 'row') setIx({ type: 'row', a: sp, b: sp });
        else if (tool === 'block') setIx({ type: 'block', a: sp, b: sp });
        else if (tool === 'stage' || tool === 'entrance') setIx({ type: 'shape', kind: tool, a: sp, b: sp });
        else if (e.pointerType !== 'mouse') touchTap.current = { sp, dbl, x: e.clientX, y: e.clientY };
        else placeAt(sp, dbl);
    };

    const onPointerMove = (e: React.PointerEvent<SVGSVGElement>) => {
        const p = toWorld(e);
        setCursor(p);
        if (pointers.current.has(e.pointerId)) pointers.current.set(e.pointerId, localPoint(e));
        if (touchTap.current && Math.hypot(e.clientX - touchTap.current.x, e.clientY - touchTap.current.y) > 8) touchTap.current = null;
        const cur = ixRef.current;
        if (cur?.type === 'pinch') {
            if (pointers.current.size < 2) return;
            const [a, b] = [...pointers.current.values()];
            const d = Math.max(1, Math.hypot(a.x - b.x, a.y - b.y));
            const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
            const nk = clampK(cur.view.k * (d / cur.dist));
            const wx = (cur.mid.x - cur.view.x) / cur.view.k;
            const wy = (cur.mid.y - cur.view.y) / cur.view.k;
            setView({ k: nk, x: mid.x - wx * nk, y: mid.y - wy * nk });
            return;
        }
        if (!cur) return;
        switch (cur.type) {
            case 'pan':
                setView({ ...cur.startView, x: cur.startView.x + e.clientX - cur.startScreen.x, y: cur.startView.y + e.clientY - cur.startScreen.y });
                break;
            case 'move': {
                let d = sub(p, cur.start);
                if (e.shiftKey) d = Math.abs(d.x) > Math.abs(d.y) ? { x: d.x, y: 0 } : { x: 0, y: d.y };
                if (settings.snap) d = { x: snapValue(d.x, settings.gridSize), y: snapValue(d.y, settings.gridSize) };
                const moved = cur.moved || dist(p, cur.start) * viewRef.current.k > 3;
                setIx({ ...cur, delta: moved ? d : { x: 0, y: 0 }, moved });
                break;
            }
            case 'marquee':
                setIx({ ...cur, b: p });
                break;
            case 'rotate': {
                const a = Math.atan2(p.y - cur.center.y, p.x - cur.center.x);
                let deg = ((a - cur.startAngle) * 180) / Math.PI;
                deg = e.shiftKey ? Math.round(deg / 15) * 15 : Math.round(deg);
                setIx({ ...cur, angle: deg });
                break;
            }
            case 'resize': {
                const sp = snapIf(p);
                let w = Math.max(cur.min.width, Math.abs(sp.x - cur.anchor.x));
                let h = Math.max(cur.min.height, Math.abs(sp.y - cur.anchor.y));
                if (e.shiftKey) {
                    if (w / h > cur.ratio) h = w / cur.ratio;
                    else w = h * cur.ratio;
                }
                setIx({
                    ...cur,
                    rect: {
                        x: sp.x < cur.anchor.x ? cur.anchor.x - w : cur.anchor.x,
                        y: sp.y < cur.anchor.y ? cur.anchor.y - h : cur.anchor.y,
                        width: w,
                        height: h,
                    },
                });
                break;
            }
            case 'row': {
                let b = snapIf(p);
                if (e.shiftKey) b = snapAngle(cur.a, p, 15);
                setIx({ ...cur, b });
                break;
            }
            case 'block':
            case 'shape':
                setIx({ ...cur, b: snapIf(p) });
                break;
        }
    };

    const onPointerUp = (e: React.PointerEvent<SVGSVGElement>) => {
        pointers.current.delete(e.pointerId);
        if (svgRef.current?.hasPointerCapture(e.pointerId)) svgRef.current.releasePointerCapture(e.pointerId);
        const cur = ixRef.current;
        if (cur?.type === 'pinch') {
            if (pointers.current.size < 2) setIx(null);
            return;
        }
        setIx(null);
        const tap = touchTap.current;
        touchTap.current = null;
        if (tap) {
            placeAt(tap.sp, tap.dbl);
            return;
        }
        if (!cur) return;
        const k = viewRef.current.k;
        switch (cur.type) {
            case 'move':
                if (cur.moved && (cur.delta.x || cur.delta.y)) onMove(cur.delta);
                else if (!cur.moved) {
                    clickFocusTimer.current = window.setTimeout(() => {
                        clickFocusTimer.current = null;
                        focusRect(selectionBoundsRef.current, 'always');
                    }, 260);
                }
                break;
            case 'marquee': {
                const r = rectFromPoints(cur.a, cur.b);
                if (r.width * k < 3 && r.height * k < 3) {
                    if (!cur.additive) onSelect({ seats: new Set(), shapes: new Set() });
                    break;
                }
                const seats = new Set(cur.additive ? selection.seats : []);
                const shapes = new Set(cur.additive ? selection.shapes : []);
                plan.zones.forEach(z => z.rows.forEach(row => row.seats.forEach(s => {
                    if (pointInRect(seatAbs(z, row, s), r)) seats.add(seatKey(s));
                })));
                (plan.stages ?? []).forEach(s => rectsIntersect(shapeBounds(s), r) && shapes.add(shapeKey('stage', s.id)));
                (plan.entrances ?? []).forEach(s => rectsIntersect(shapeBounds(s), r) && shapes.add(shapeKey('entrance', s.id)));
                if (seats.size || shapes.size) pendingFocus.current = 'ifNeeded';
                onSelect({ seats, shapes });
                break;
            }
            case 'rotate':
                if (cur.angle) onRotate(cur.center, cur.angle);
                break;
            case 'resize':
                if (cur.table) onResizeTable(cur.key, cur.rect);
                else onResizeShape(cur.key, cur.rect);
                break;
            case 'row':
                if (dist(cur.a, cur.b) >= settings.seatSpacing * 0.5) onDrawRows([linePoints(cur.a, cur.b, settings.seatSpacing)]);
                break;
            case 'block': {
                const r = rectFromPoints(cur.a, cur.b);
                if (r.width >= settings.seatSpacing * 0.5 || r.height >= settings.rowSpacing * 0.5) {
                    onDrawRows(blockRows(cur.a, cur.b, settings.seatSpacing, settings.rowSpacing));
                }
                break;
            }
            case 'shape': {
                let r = rectFromPoints(cur.a, cur.b);
                if (r.width * k < 6 || r.height * k < 6) {
                    const w = cur.kind === 'stage' ? 240 : 100;
                    const h = cur.kind === 'stage' ? 80 : 36;
                    r = { x: cur.a.x - w / 2, y: cur.a.y - h / 2, width: w, height: h };
                }
                onDrawShape(cur.kind, r);
                break;
            }
        }
    };

    const preview = useMemo(() => {
        if (ix?.type === 'move' && ix.moved) return (p: Point) => add(p, ix.delta);
        if (ix?.type === 'rotate' && ix.angle) return (p: Point) => rotatePoint(p, ix.center, ix.angle);
        return undefined;
    }, [ix]);

    const draftRows = useMemo(() => {
        if (ix?.type === 'row') return dist(ix.a, ix.b) >= settings.seatSpacing * 0.5 ? [linePoints(ix.a, ix.b, settings.seatSpacing)] : [];
        if (ix?.type === 'block') return blockRows(ix.a, ix.b, settings.seatSpacing, settings.rowSpacing);
        return [];
    }, [ix, settings.seatSpacing, settings.rowSpacing]);

    const tableGhost = tool === 'table' && cursor && !spaceDown
        ? tableLayout(settings.tableShape, snapIf(cursor), settings.tableSeats, settings.seatSpacing, undefined, settings.tableShape === 'rect' ? settings.tableEndSeats : 0)
        : ix?.type === 'resize' && ix.table
            ? tableLayout(ix.table.shape, { x: ix.rect.x + ix.rect.width / 2, y: ix.rect.y + ix.rect.height / 2 }, ix.table.count, settings.seatSpacing, ix.rect, ix.table.ends)
            : null;

    const k = view.k;
    const showNumbers = settings.showSeatNumbers && k >= 0.7;
    const showLabels = k >= 0.35;
    const gridStep = settings.gridSize * k;
    const majorStep = gridStep * 5;
    const showMinor = gridStep >= 8;

    const panCursor = tool === 'pan' || spaceDown;
    const cursorClass = ix?.type === 'pan'
        ? 'cursor-grabbing'
        : panCursor
            ? 'cursor-grab'
            : tool === 'select'
                ? 'cursor-default'
                : 'cursor-crosshair';

    const renderShapes = (kind: ShapeKind, list: Shape[] = []) =>
        list.map(s => {
            const key = shapeKey(kind, s.id);
            const sel = selection.shapes.has(key);
            const shown = sel && ix && (ix.type !== 'resize' || ix.key === key) ? previewShape(s, ix) : s;
            return <ShapeView key={key} shape={shown} kind={kind} selected={sel} />;
        });

    const bounds = selectionBounds && preview && ix?.type === 'move'
        ? { ...selectionBounds, x: selectionBounds.x + (ix.delta.x), y: selectionBounds.y + ix.delta.y }
        : selectionBounds;
    const handleSize = (coarse ? 18 : 8) / k;
    const rotateOffset = (coarse ? 36 : 24) / k;
    const showHandles = tool === 'select' && bounds && (!ix || ix.type === 'rotate');
    const resizeRect = singleShape ? (ix?.type === 'resize' ? ix.rect : { ...singleShape.shape.position, ...singleShape.shape.size }) : null;

    return (
        <div ref={wrapRef} className="relative flex-1 min-w-0 overflow-hidden bg-[#F4F5F7] dark:bg-[#E2E5E9]">
            <svg
                ref={svgRef}
                width={size.width}
                height={size.height}
                className={`block touch-none select-none ${cursorClass}`}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={e => {
                    pointers.current.delete(e.pointerId);
                    touchTap.current = null;
                    setIx(null);
                }}
                onPointerLeave={() => setCursor(null)}
                onContextMenu={e => e.preventDefault()}
            >
                <defs>
                    <pattern id={`${gridId}-minor`} width={gridStep} height={gridStep} patternUnits="userSpaceOnUse" x={view.x} y={view.y}>
                        <path d={`M ${gridStep} 0 L 0 0 0 ${gridStep}`} fill="none" className="stroke-gray-300/60" strokeWidth={1} />
                    </pattern>
                    <pattern id={`${gridId}-major`} width={majorStep} height={majorStep} patternUnits="userSpaceOnUse" x={view.x} y={view.y}>
                        <path d={`M ${majorStep} 0 L 0 0 0 ${majorStep}`} fill="none" className="stroke-gray-300" strokeWidth={1} />
                    </pattern>
                </defs>
                {settings.showGrid && (
                    <>
                        {showMinor && <rect width="100%" height="100%" fill={`url(#${gridId}-minor)`} pointerEvents="none" />}
                        {majorStep >= 8 && <rect width="100%" height="100%" fill={`url(#${gridId}-major)`} pointerEvents="none" />}
                    </>
                )}

                <g transform={`translate(${view.x} ${view.y}) scale(${k})`}>
                    {renderShapes('stage', plan.stages)}
                    {renderShapes('entrance', plan.entrances)}

                    {plan.zones.map((zone, zi) => (
                        <g key={zi}>
                            {sectionAnchors[zi] && showLabels && (() => {
                                const a = sectionAnchors[zi]!;
                                const p = preview && selectedZones.has(zi) ? preview(a.point) : a.point;
                                return (
                                    <text
                                        data-zone={zi}
                                        x={p.x}
                                        y={p.y}
                                        dy=".35em"
                                        textAnchor="middle"
                                        fontSize={16}
                                        fontWeight={700}
                                        transform={`rotate(${a.rotation} ${p.x} ${p.y})`}
                                        className={selectedZones.has(zi) ? 'fill-sky-600 cursor-pointer select-none' : 'fill-gray-700 cursor-pointer select-none'}
                                    >
                                        {zone.name}
                                    </text>
                                );
                            })()}
                            {zone.rows.map(row => {
                                if (ix?.type === 'resize' && ix.table && row.tableId && ix.key === `stage:${row.tableId}`) return null;
                                const sel = selectionByRow.get(row.uuid!);
                                return (
                                    <RowView
                                        key={row.uuid}
                                        row={row}
                                        zone={zone}
                                        colors={colors}
                                        selected={sel}
                                        preview={sel ? preview : undefined}
                                        showNumbers={showNumbers}
                                        showLabels={showLabels}
                                    />
                                );
                            })}
                        </g>
                    ))}

                    {draftRows.map((pts, i) => pts.map((p, j) => (
                        <circle key={`d${i}-${j}`} cx={p.x} cy={p.y} r={SEAT_RADIUS} fill="#0EA5E9" fillOpacity={0.25} stroke="#0EA5E9" strokeWidth={1.5 / k} pointerEvents="none" />
                    )))}
                    {ix?.type === 'row' && (
                        <text x={ix.b.x + 14 / k} y={ix.b.y - 14 / k} fontSize={12 / k} className="fill-sky-600" pointerEvents="none">
                            {draftRows[0]?.length ?? 0} seats
                        </text>
                    )}
                    {ix?.type === 'block' && draftRows.length > 0 && (
                        <text x={ix.b.x + 14 / k} y={ix.b.y + 14 / k} fontSize={12 / k} className="fill-sky-600" pointerEvents="none">
                            {draftRows.length} x {draftRows[0].length} = {draftRows.length * draftRows[0].length} seats
                        </text>
                    )}
                    {ix?.type === 'shape' && (
                        <rect {...rectFromPoints(ix.a, ix.b)} fill="#0EA5E9" fillOpacity={0.12} stroke="#0EA5E9" strokeWidth={1.5 / k} strokeDasharray={`${4 / k},${3 / k}`} pointerEvents="none" />
                    )}

                    {tableGhost && (
                        <g pointerEvents="none" opacity={0.55}>
                            {tableGhost.polygon
                                ? <polygon points={tableGhost.polygon.map(p => `${p.x + tableGhost.rect.x},${p.y + tableGhost.rect.y}`).join(' ')} fill="#E5E7EB" stroke="#0EA5E9" strokeWidth={1.5 / k} />
                                : tableGhost.type === 'circle'
                                ? <ellipse cx={tableGhost.rect.x + tableGhost.rect.width / 2} cy={tableGhost.rect.y + tableGhost.rect.height / 2} rx={tableGhost.rect.width / 2} ry={tableGhost.rect.height / 2} fill="#E5E7EB" stroke="#0EA5E9" strokeWidth={1.5 / k} />
                                : <rect {...tableGhost.rect} rx={6} fill="#E5E7EB" stroke="#0EA5E9" strokeWidth={1.5 / k} />}
                            {tableGhost.points.map((p, i) => (
                                <circle key={i} cx={p.x} cy={p.y} r={SEAT_RADIUS} fill="#0EA5E9" fillOpacity={0.25} stroke="#0EA5E9" strokeWidth={1.5 / k} />
                            ))}
                        </g>
                    )}

                    {polygon.length > 0 && (
                        <g pointerEvents="none">
                            <polyline
                                points={[...polygon, ...(cursor ? [snapIf(cursor)] : [])].map(p => `${p.x},${p.y}`).join(' ')}
                                fill="#0EA5E9"
                                fillOpacity={0.1}
                                stroke="#0EA5E9"
                                strokeWidth={1.5 / k}
                            />
                            {polygon.map((p, i) => (
                                <circle key={i} cx={p.x} cy={p.y} r={(i === 0 ? 6 : 4) / k} fill={i === 0 ? '#0EA5E9' : '#fff'} stroke="#0EA5E9" strokeWidth={1.5 / k} />
                            ))}
                        </g>
                    )}

                    {ix?.type === 'marquee' && (
                        <rect {...rectFromPoints(ix.a, ix.b)} fill="#0EA5E9" fillOpacity={0.08} stroke="#0EA5E9" strokeWidth={1 / k} pointerEvents="none" />
                    )}

                    {showHandles && bounds && (
                        <g>
                            {ix?.type === 'rotate' ? (
                                <>
                                    <line x1={ix.center.x} y1={ix.center.y} x2={cursor?.x ?? ix.center.x} y2={cursor?.y ?? ix.center.y} stroke="#0EA5E9" strokeWidth={1 / k} strokeDasharray={`${4 / k},${3 / k}`} />
                                    <text x={ix.center.x} y={ix.center.y - 10 / k} fontSize={12 / k} textAnchor="middle" className="fill-sky-600">{ix.angle} deg</text>
                                </>
                            ) : (
                                <>
                                    <rect x={bounds.x} y={bounds.y} width={bounds.width} height={bounds.height} fill="none" stroke="#0EA5E9" strokeWidth={1 / k} strokeDasharray={`${4 / k},${3 / k}`} pointerEvents="none" />
                                    <line x1={bounds.x + bounds.width / 2} y1={bounds.y} x2={bounds.x + bounds.width / 2} y2={bounds.y - rotateOffset} stroke="#0EA5E9" strokeWidth={1 / k} pointerEvents="none" />
                                    <circle data-handle="rotate" cx={bounds.x + bounds.width / 2} cy={bounds.y - rotateOffset} r={(coarse ? 12 : 6) / k} fill="#fff" stroke="#0EA5E9" strokeWidth={1.5 / k} className="cursor-grab" />
                                </>
                            )}
                        </g>
                    )}
                    {tool === 'select' && resizeRect && (!ix || ix.type === 'resize') && (
                        <g>
                            {(['nw', 'ne', 'sw', 'se'] as const).map(h => (
                                <rect
                                    key={h}
                                    data-handle={h}
                                    x={(h.includes('w') ? resizeRect.x : resizeRect.x + resizeRect.width) - handleSize / 2}
                                    y={(h.includes('n') ? resizeRect.y : resizeRect.y + resizeRect.height) - handleSize / 2}
                                    width={handleSize}
                                    height={handleSize}
                                    fill="#fff"
                                    stroke="#0EA5E9"
                                    strokeWidth={1.5 / k}
                                    className={h === 'nw' || h === 'se' ? 'cursor-nwse-resize' : 'cursor-nesw-resize'}
                                />
                            ))}
                        </g>
                    )}
                </g>
            </svg>

            <div className="pointer-events-none absolute left-3 bottom-3 right-40 hidden lg:flex items-center gap-3 text-[11px] text-gray-500 dark:text-nightMuted">
                <span className="rounded bg-white/90 px-2 py-1 font-mono shadow-sm dark:bg-nightElevated/90">
                    {cursor ? `${Math.round(cursor.x)}, ${Math.round(cursor.y)}` : '-, -'}
                </span>
                <span className="truncate rounded bg-white/90 px-2 py-1 shadow-sm dark:bg-nightElevated/90">{TOOL_HINTS[tool]}</span>
            </div>

            <div className="absolute right-3 bottom-3 flex items-center rounded-md border border-gray-200 bg-white shadow-sm dark:border-nightBorder dark:bg-nightElevated">
                <button type="button" title="Zoom out (Ctrl -)" className="h-8 w-8 flex items-center justify-center text-gray-600 hover:bg-gray-100 dark:text-nightTextSecondary dark:hover:bg-nightSurface" onClick={() => zoomAt(1 / 1.25)}>
                    <Minus className="h-3.5 w-3.5" />
                </button>
                <button type="button" title="Reset to 100% (Shift 0)" className="h-8 min-w-[52px] px-1 text-xs font-medium tabular-nums text-gray-700 hover:bg-gray-100 dark:text-nightTextSecondary dark:hover:bg-nightSurface" onClick={() => zoomAt(1 / k)}>
                    {Math.round(k * 100)}%
                </button>
                <button type="button" title="Zoom in (Ctrl +)" className="h-8 w-8 flex items-center justify-center text-gray-600 hover:bg-gray-100 dark:text-nightTextSecondary dark:hover:bg-nightSurface" onClick={() => zoomAt(1.25)}>
                    <Plus className="h-3.5 w-3.5" />
                </button>
                <div className="h-5 w-px bg-gray-200 dark:bg-nightBorder" />
                <button
                    type="button"
                    title="Zoom to selection (Shift 2)"
                    disabled={!selectionBounds}
                    className="h-8 w-8 flex items-center justify-center text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:pointer-events-none dark:text-nightTextSecondary dark:hover:bg-nightSurface"
                    onClick={() => focusRect(selectionBounds, 'always')}
                >
                    <ScanSearch className="h-3.5 w-3.5" />
                </button>
                <button type="button" title="Fit to content (Shift 1)" className="h-8 w-8 flex items-center justify-center text-gray-600 hover:bg-gray-100 dark:text-nightTextSecondary dark:hover:bg-nightSurface" onClick={fit}>
                    <Maximize className="h-3.5 w-3.5" />
                </button>
            </div>
        </div>
    );
});

Canvas.displayName = 'SeatMapCanvas';

export default Canvas;
